import type { EditorDocument, Note } from "@hq/contracts";
import { newId } from "./ids";
import { normalizeTags } from "./tags";
import type { NowContext } from "./time";

export type NewNoteInput = {
  id?: string;
  title?: string;
  markdown?: string;
  folderId?: string;
  parentNoteId?: string;
  pinned?: boolean;
  tags?: readonly string[];
};

export const UNTITLED_NOTE = "Untitled";

/** The first heading or non-empty line of the markdown, for notes whose title follows the body. */
export function deriveTitle(markdown: string): string {
  for (const raw of markdown.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    const text = (heading ? heading[1]! : line).replace(/[*_`~]/g, "").trim();
    if (text) return text.length > 120 ? `${text.slice(0, 117)}…` : text;
  }
  return UNTITLED_NOTE;
}

export function createNote(input: NewNoteInput, ctx: NowContext): Note {
  const markdown = input.markdown ?? "";
  const note: Note = {
    id: input.id ?? newId(),
    createdAt: ctx.now,
    updatedAt: ctx.now,
    revision: 0,
    title: input.title?.trim() || deriveTitle(markdown),
    markdown,
  };
  if (input.folderId) note.folderId = input.folderId;
  if (input.parentNoteId) note.parentNoteId = input.parentNoteId;
  if (input.pinned) note.pinned = true;
  const tags = input.tags ? normalizeTags(input.tags) : [];
  if (tags.length > 0) note.tags = tags;
  return note;
}

export function setNoteTags(note: Note, tags: readonly string[], ctx: NowContext): Note {
  const next: Note = { ...note, updatedAt: ctx.now };
  const normalized = normalizeTags(tags);
  if (normalized.length > 0) next.tags = normalized;
  else delete next.tags;
  return next;
}

/** Titles that were derived follow the body; explicitly renamed titles stick. */
function followTitle(note: Note, next: Note): Note {
  if (note.title === UNTITLED_NOTE || note.title === deriveTitle(note.markdown)) {
    next.title = deriveTitle(next.markdown);
  }
  return next;
}

/** Whether two editor documents hold the same content. Absent equals absent only. */
export function sameDocument(
  a: EditorDocument | undefined,
  b: EditorDocument | undefined,
): boolean {
  if (a === undefined || b === undefined) return a === b;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Plain Markdown, from a writer that has no editor document. The document, if there was one, no
 * longer says what the note says, so it goes: the Markdown is the whole note again.
 */
export function updateNoteMarkdown(note: Note, markdown: string, ctx: NowContext): Note {
  if (markdown === note.markdown) return note;
  const next: Note = { ...note, markdown, updatedAt: ctx.now };
  delete next.document;
  return followTitle(note, next);
}

/** The editor's document, with the Markdown mirror written from it. */
export function updateNoteContent(
  note: Note,
  content: { document: EditorDocument; markdown: string },
  ctx: NowContext,
): Note {
  if (content.markdown === note.markdown && sameDocument(content.document, note.document))
    return note;
  const next: Note = {
    ...note,
    markdown: content.markdown,
    document: content.document,
    updatedAt: ctx.now,
  };
  return followTitle(note, next);
}

export function renameNote(note: Note, title: string, ctx: NowContext): Note {
  return { ...note, title: title.trim() || UNTITLED_NOTE, updatedAt: ctx.now };
}

export function moveNote(note: Note, folderId: string | null, ctx: NowContext): Note {
  const next: Note = { ...note, updatedAt: ctx.now };
  if (folderId === null) delete next.folderId;
  else next.folderId = folderId;
  return next;
}

export function setNotePinned(note: Note, pinned: boolean, ctx: NowContext): Note {
  const next: Note = { ...note, updatedAt: ctx.now };
  if (pinned) next.pinned = true;
  else delete next.pinned;
  return next;
}

export function deleteNote(note: Note, ctx: NowContext): Note {
  return { ...note, deletedAt: ctx.now, updatedAt: ctx.now };
}

export function restoreNote(note: Note, ctx: NowContext): Note {
  const next: Note = { ...note, updatedAt: ctx.now };
  delete next.deletedAt;
  return next;
}

export type NoteSort = "updatedAt" | "createdAt" | "title" | "manual";

/** Pinned first, then by the chosen key. Written once here, not per store. */
export function sortNotes(notes: readonly Note[], by: NoteSort = "updatedAt"): Note[] {
  if (by === "manual" && notes.every((note) => note.sortOrder === undefined))
    return sortNotes(notes);
  return [...notes].sort((a, b) => {
    if (by === "manual") {
      return (
        (a.sortOrder ?? -Date.parse(a.createdAt)) - (b.sortOrder ?? -Date.parse(b.createdAt)) ||
        a.id.localeCompare(b.id)
      );
    }
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    if (by === "title") return a.title.localeCompare(b.title);
    return a[by] < b[by] ? 1 : a[by] > b[by] ? -1 : 0;
  });
}

/** Daily-note title and lookup key: `2026-09-16`. */
export function dailyNoteTitle(date: string): string {
  return date;
}

/** A portable Markdown link to a note, labelled with its title. */
export function noteLink(note: Pick<Note, "id" | "title">): string {
  return `[${note.title.replace(/[\\[\]]/g, "\\$&")}](/notes/${note.id})`;
}

/**
 * The child note for writing moved out of `source`: its own note, filed beside the source and
 * pointing back at it. The source is rewritten by the editor, which replaces what moved with
 * `noteLink(child)`.
 */
export function extractNote(
  source: Note,
  input: { id?: string; title: string; markdown: string },
  ctx: NowContext,
): Note {
  if (!input.markdown.trim()) throw new Error("Select some writing to move");
  return createNote(
    {
      ...(input.id ? { id: input.id } : {}),
      title: input.title,
      markdown: input.markdown,
      parentNoteId: source.id,
      ...(source.folderId ? { folderId: source.folderId } : {}),
    },
    ctx,
  );
}
